export const LOW_MARGIN_THRESHOLD_PCT = 10;

export type PriceHealthStatus = 'unknown' | 'loss' | 'low-margin' | 'healthy';

export interface PriceHealth {
  status: PriceHealthStatus;
  profit: number | null;
  marginPct: number | null;
}

export function relativeToAbsoluteFactors(
  relativeQuantities: readonly number[],
): number[] {
  let cumulative = 1;
  return relativeQuantities.map((quantity, index) => {
    if (index === 0) return 1;
    if (!Number.isFinite(quantity) || quantity <= 0) return Number.NaN;
    cumulative *= quantity;
    return cumulative;
  });
}

export function absoluteToRelativeFactors(
  absoluteFactors: readonly number[],
): number[] {
  return absoluteFactors.map((factor, index) => {
    if (index === 0) return 1;
    const previous = absoluteFactors[index - 1];
    if (
      !Number.isFinite(factor) ||
      !Number.isFinite(previous) ||
      factor <= 0 ||
      previous <= 0
    ) {
      return Number.NaN;
    }
    return factor / previous;
  });
}

export function baseCostFromSelectedUnit(
  selectedUnitCost: number,
  selectedUnitFactor: number,
): number {
  if (
    !Number.isFinite(selectedUnitCost) ||
    !Number.isFinite(selectedUnitFactor) ||
    selectedUnitCost < 0 ||
    selectedUnitFactor <= 0
  ) {
    return 0;
  }
  return Math.round(selectedUnitCost / selectedUnitFactor);
}

export function derivedUnitCost(baseCost: number, factor: number): number {
  if (
    !Number.isFinite(baseCost) ||
    !Number.isFinite(factor) ||
    baseCost < 0 ||
    factor <= 0
  ) {
    return 0;
  }
  return Math.round(baseCost * factor);
}

export function classifyPriceHealth(
  salePrice: number,
  cost: number,
  lowMarginThresholdPct = LOW_MARGIN_THRESHOLD_PCT,
): PriceHealth {
  if (salePrice <= 0 || cost <= 0) {
    return { status: 'unknown', profit: null, marginPct: null };
  }

  const profit = salePrice - cost;
  const marginPct = (profit / salePrice) * 100;
  if (profit < 0) return { status: 'loss', profit, marginPct };
  if (marginPct < lowMarginThresholdPct) {
    return { status: 'low-margin', profit, marginPct };
  }
  return { status: 'healthy', profit, marginPct };
}
